import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ScScoreReCaptcha } from '@semantic-components/re-captcha';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { PAGE } from '../app.routes';
import { APP_CONFIG, ApiResponse, ApiResponseStatusCode, COUNTRY_DICTIONARY, Country, DateOnly, DictionaryEnum, DocumentItemType, LanguageService, LegalDocument, LegalDocumentAcceptance, LegalDocumentType, LoaderService, NO_ITEMS, Practice, PracticeType, RoleInformationType, RoleTypeEnum, SURGEON_CODE, ToastService, USA_CODE, USER_REQUEST_DICTIONARY_LIST, USState, UsStateLicense, UserProfile, UserRepository, UserRequest, YES_ITEMS, countryMapper, usStateMapper } from '../core';
import { UserRequestForm } from '../models';


/**
* This services handles user account request.
*/
@Injectable({ providedIn: 'root' })
export class UserRequestService {

	private readonly router = inject(Router);
	private readonly config = inject(APP_CONFIG);
	private readonly toastSrv = inject(ToastService);
	private readonly langSrv = inject(LanguageService);
	private readonly userRepo = inject(UserRepository);
	private readonly loaderSrv = inject(LoaderService);
	private readonly recaptchaSrv = inject(ScScoreReCaptcha);

	private _userRequestForm: UserRequestForm | null = null;
	private _userRequest: UserRequest | null = null;
	private _legalDocumentToAccept: LegalDocument[] | null = null;
	private _legalDocCountryCode: string | null = null;

	private _allCountries: Country[] = [];
	private _allUSStates: USState[] = [];
	private _allRoleTypes: RoleInformationType[] = [];
	private _allPracticeTypes: PracticeType[] = [];
	private _legalDocumentTypes: LegalDocumentType[] = [];
	private _documentItemTypes: DocumentItemType[] = [];

	private readonly _emailExistError = new BehaviorSubject<boolean | null>(null);
	private readonly _successState = new BehaviorSubject<boolean | null>(null);

	private readonly CAPTCHA_ACTION_REQUEST = 'user_request';

	/**
	* Get email already exist error status.
	*/
	get emailExistError(): Observable<boolean | null> {
		return this._emailExistError.asObservable();
	}

	/**
	* Get user request success status.
	*/
	get successState(): Observable<boolean | null> {
		return this._successState.asObservable();
	}

	/**
	* Get ordered list of countries.
	*/
	getCountries(): Observable<Country[]> {
		return this.userRepo.getDictionaries(COUNTRY_DICTIONARY).pipe(
			map(res => this.handleApiResponse(res)),
			filter(list => !!list && list.length === 1),
			map(list => list[0].values),
			map(list => list.map(i => countryMapper(i))),
			map(list => [...list].sort((a, b) => new Intl.Collator().compare(a.name, b.name)))
		);
	}

	/**
	* Init all dictionaries for user request.
	*/
	initDictionaries(): Observable<boolean> {
		if (this._allCountries.length > 0 && this._allUSStates.length > 0 && this._allRoleTypes.length > 0 && this._allPracticeTypes.length > 0 && this._legalDocumentTypes.length > 0 && this._documentItemTypes.length > 0) {
			return of(true);
		}
		return this.userRepo.getDictionaries(USER_REQUEST_DICTIONARY_LIST).pipe(
			map(res => this.handleApiResponse(res)),
			filter(list => !!list?.length),
			tap(list => {
				this._allCountries = list.find(x => x.name == DictionaryEnum.COUNTRY)?.values.map(country => countryMapper(country)) ?? [];
				this._allUSStates = list.find(x => x.name == DictionaryEnum.US_STATE)?.values.map(state => usStateMapper(state)) ?? [];
				this._allRoleTypes = list.find(x => x.name == DictionaryEnum.ROLE_INFORMATION_TYPE)?.values ?? [];
				this._allPracticeTypes = list.find(x => x.name == DictionaryEnum.PRACTICE_TYPE)?.values ?? [];
				this._legalDocumentTypes = list.find(x => x.name == DictionaryEnum.LEGAL_DOCUMENT_TYPE)?.values ?? [];
				this._documentItemTypes = list.find(x => x.name == DictionaryEnum.DOCUMENT_ITEM_TYPE)?.values ?? [];
				this._allCountries.sort((a, b) => new Intl.Collator().compare(a.name, b.name));
				this._allUSStates.sort((a, b) => new Intl.Collator().compare(a.name, b.name));
			}),
			map(() => true)
		);
	}

	/**
	* Get current Country list.
	*/
	getAllCountries(): Country[] {
		return this._allCountries;
	}

	/**
	* Get current Unites States list.
	*/
	getAllUSStates(): USState[] {
		return this._allUSStates;
	}

	/**
	* Get current Role Information Type list.
	*/
	getAllRoleTypes(): RoleInformationType[] {
		return this._allRoleTypes;
	}

	/**
	* Get current Practice Type list.
	*/
	getAllPracticeTypes(): PracticeType[] {
		return this._allPracticeTypes;
	}

	/**
	* Get current Legal Document Type list.
	*/
	getLegalDocumentTypes(): LegalDocumentType[] {
		return this._legalDocumentTypes;
	}

	/**
	* Get current Document Item Type list.
	*/
	getDocumentItemTypes(): DocumentItemType[] {
		return this._documentItemTypes;
	}

	/**
	* Get current User Request form.
	*/
	getUserRequestForm(): UserRequestForm | null {
		return this._userRequestForm;
	}

	/**
	* Get current legal documents to accept.
	*/
	getLegalDocumentToAccept(): LegalDocument[] | null {
		return this._legalDocumentToAccept;
	}

	/**
	* Set current legal documents to accept.
	*/
	setLegalDocumentToAccept(docs: LegalDocument[], countryCode: string): void {
		this._legalDocumentToAccept = docs;
		this._legalDocCountryCode = countryCode;
	}

	/**
	* Get yes label according current country code.
	*/
	getYesItem(): string {
		return YES_ITEMS.get(`Yes_${this._legalDocCountryCode?.toLowerCase()}`) ?? YES_ITEMS.get('Yes_en') ?? '';
	}

	/**
	* Get no label according current country code.
	*/
	getNoItem(): string {
		return NO_ITEMS.get(`No_${this._legalDocCountryCode?.toLowerCase()}`) ?? NO_ITEMS.get('No_en') ?? '';
	}

	/**
	* Handle user request after form submitting.
	*/
	handleFormSubmit(requestForm: UserRequestForm): void {
		this._userRequestForm = requestForm;
		this._userRequest = this.createUserRequest(this._userRequestForm);
		this.loaderSrv.show();
		this.getLegalDocuments(this._userRequest.countryCode, this._userRequest.roleInformationType).pipe(
			finalize(() => this.loaderSrv.hide())
		).subscribe({
			next: legalDocs => {
				if (legalDocs?.length) {
					this._legalDocumentToAccept = legalDocs;
					this._legalDocCountryCode = this._userRequest!.countryCode;
					this.router.navigate([PAGE.REQUEST_LEGAL_TERMS]);
				} else {
					this._handleUserRequest();
				}
			}, error: () => this.toastSrv.showError(this.langSrv.labels().USER_REQUEST_ERROR_MSG)
		});
	}

	/**
	* Handle legal documents acceptance.
	*/
	handleLegalDocAcceptance(legalDocAcceptance: LegalDocumentAcceptance[], isAcceptance?: boolean): void {
		if (isAcceptance) {
			this._handleLegalDocsAcceptance(legalDocAcceptance);
		} else {
			this._userRequest!.legalDocumentAccepted = legalDocAcceptance;
			this._handleUserRequest();
		}
	}

	private _handleUserRequest(): void {
		this.loaderSrv.show();
		from(this.recaptchaSrv.execute(this.CAPTCHA_ACTION_REQUEST)).pipe(
			switchMap(token => this.sendRequest(token)),
			finalize(() => this.loaderSrv.hide())
		).subscribe({
			next: () => {
				this.resetRequest();
				this._successState.next(true);
				this.router.navigateByUrl(PAGE.REQUEST_SUCCESS);
			},
			error: (err: Error) => {
				if (err?.message === 'UserNameNotUnique') {
					this.toastSrv.showError(this.langSrv.labels().USER_REQUEST_ERROR_EMAIL);
					this._emailExistError.next(true);
				} else if (err?.message === 'TokenNotValid') {
					this.toastSrv.showError(this.langSrv.labels().USER_REQUEST_ERROR_TOKEN);
				} else {
					this.toastSrv.showError(this.langSrv.labels().USER_REQUEST_ERROR_MSG);
				}
			}
		});
	}

	private _handleLegalDocsAcceptance(legalDocAcceptance: LegalDocumentAcceptance[]): void {
		this.loaderSrv.show();
		this.setDocumentsApproval(legalDocAcceptance).subscribe({
			next: () => {
				this.toastSrv.showSuccess(this.langSrv.labels().USER_REQUEST_SUCCESS_MSG);
				setTimeout(() => globalThis.location.href = this.config.patplanSite, 3000);
			},
			error: () => {
				this.toastSrv.showError(this.langSrv.labels().USER_REQUEST_ERROR_MSG);
				this.loaderSrv.hide();
			}
		});
	}

	/**
	* Get legal documents to accept.
	*/
	getLegalDocuments(countryCode: string, roleType: string): Observable<LegalDocument[]> {
		return this.userRepo.getLegalDocuments(countryCode, roleType).pipe(
			map(res => this.handleApiResponse(res))
		);
	}

	/**
	 * Approve legal documents.
	 */
	private setDocumentsApproval(legalDocsAccepted: LegalDocumentAcceptance[]): Observable<any> {
		return this.userRepo.setDocumentsApproval(legalDocsAccepted).pipe(
			map(res => this.handleApiResponse(res))
		);
	}

	private sendRequest(captchaToken: string): Observable<any> {
		return this.userRepo.sendRequest(this._userRequest!, captchaToken).pipe(
			map(res => this.handleApiResponse(res))
		);
	}

	private resetRequest(): void {
		this._userRequestForm = null;
		this._userRequest = null;
		this._legalDocumentToAccept = null;
		this._legalDocCountryCode = null;
	}

	private createUserRequest(form: UserRequestForm): UserRequest {
		let practice: Practice | null = null;
		if (form.practiceType != null) {
			let licenseList: UsStateLicense[] = [];
			for (let i = 1; i <= 5; i++) {
				if ((form as any)['stateCode' + i] == null) break;
				let license: UsStateLicense = { stateCode: (form as any)['stateCode' + i], licenseNumber: (form as any)['licenseNumber' + i] };
				licenseList.push(license);
			}
			practice = {
				practiceType: form.practiceType,
				npi: form.npi,
				stateLicenses: licenseList
			}

		}
		return {
			email: form.email,
			confirmEmail: form.confirmEmail,
			firstName: form.firstName,
			lastName: form.lastName,
			dateOfBirth: new DateOnly(form.dateOfBirth),
			countryCode: form.country.code,
			roleInformationType: form.roleType == RoleTypeEnum.HCP ? SURGEON_CODE : form.otherRole,
			practice: practice,
			distributor: form.distributor,
			hospitalOrCompany: this.getHospitalOrCompany(form),
			address: form.address,
			city: form.city,
			stateProvince: form.country?.code == USA_CODE ? form.usStateProvince.name : form.stateProvince,
			postalCode: form.postalCode,
			officePhone: form.officePhone,
			mobilePhone: form.mobilePhone,
			legalDocumentAccepted: null
		};
	}

	private getHospitalOrCompany(form: UserRequestForm): string {
		if (form.hospital != null) {
			return form.hospital instanceof Object ? form.hospital.poi!.name! : form.hospital;
		} else if (form.company != null) {
			return form.company instanceof Object ? form.company.poi!.name! : form.company;
		}
		return '';
	}

	/**
	 * Get user profile data.
	 */
	getUserProfile(): Observable<UserProfile> {
		return this.userRepo.getUserProfile().pipe(
			map(res => this.handleApiResponse(res))
		);
	}

	private handleApiResponse<T>(response: ApiResponse<T>): T {
		switch (response.statusCode) {
			case ApiResponseStatusCode.Success: return response.result as T;
			case ApiResponseStatusCode.UserNameNotUnique: throw new Error('UserNameNotUnique');
			case ApiResponseStatusCode.TokenNotValid: throw new Error('TokenNotValid');
			default: throw new Error('Generic error');
		}
	}

}
